import { useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import SASidebar from '@/pages/sa/dashboard/components/SASidebar';
import { getRadioQrById, type RadioQrData } from '@/mocks/radioQr';

export default function SARadioQrPage() {
  const { id } = useParams<{ id: string }>();
  const radioId = Number(id);
  const qrData: RadioQrData | undefined = getRadioQrById(radioId);

  const [showDestEditor, setShowDestEditor] = useState(false);
  const [destType, setDestType] = useState<'custom_domain' | 'rfe_page'>(
    qrData?.currentDestination.type || 'rfe_page'
  );
  const [customDomainInput, setCustomDomainInput] = useState(qrData?.customDomain || '');

  const handleCopyUrl = useCallback(async () => {
    if (!qrData) return;
    const fullUrl = `https://${qrData.permanentUrl}`;
    try {
      await navigator.clipboard.writeText(fullUrl);
      Swal.fire({
        title: 'URL copiada',
        html: `<p style="font-size:13px;color:#6b7280"><code style="background:#f3f4f6;padding:2px 6px;border-radius:4px;font-size:12px">${fullUrl}</code></p>`,
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
        customClass: {
          popup: 'rounded-2xl',
          title: 'font-bold text-sm',
          htmlContainer: 'text-sm',
        },
      });
    } catch {
      Swal.fire({
        title: 'Copiado manual',
        html: `<p style="font-size:12px;word-break:break-all;color:#374151">${fullUrl}</p>`,
        icon: 'info',
        customClass: {
          popup: 'rounded-2xl',
          title: 'font-bold text-sm',
          confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap bg-foreground-950 text-background-50',
        },
        buttonsStyling: false,
      });
    }
  }, [qrData]);

  const handleDownloadPng = useCallback(() => {
    if (!qrData) return;
    Swal.fire({
      title: 'Descargar QR como PNG',
      html: `<p style="font-size:12px;color:#6b7280">Simulando descarga de <strong>qr-${qrData.redirectCode}.png</strong></p><p style="font-size:10px;color:#9ca3af;margin-top:4px">Resolucion: 1024 × 1024 px</p>`,
      icon: 'success',
      timer: 2000,
      showConfirmButton: false,
      customClass: {
        popup: 'rounded-2xl',
        title: 'font-bold text-sm',
        htmlContainer: 'text-sm',
      },
    });
  }, [qrData]);

  const handleDownloadSvg = useCallback(() => {
    if (!qrData) return;
    Swal.fire({
      title: 'Descargar QR como SVG',
      html: `<p style="font-size:12px;color:#6b7280">Simulando descarga de <strong>qr-${qrData.redirectCode}.svg</strong></p><p style="font-size:10px;color:#9ca3af;margin-top:4px">Formato vectorial escalable</p>`,
      icon: 'success',
      timer: 2000,
      showConfirmButton: false,
      customClass: {
        popup: 'rounded-2xl',
        title: 'font-bold text-sm',
        htmlContainer: 'text-sm',
      },
    });
  }, [qrData]);

  const handleUpdateDestination = useCallback(() => {
    if (!qrData) return;
    const label = destType === 'custom_domain'
      ? (customDomainInput || 'dominio-personalizado.com')
      : 'Perfil en RadioFrecuenciaWeb';
    const url = destType === 'custom_domain'
      ? `https://${customDomainInput || 'dominio-personalizado.com'}`
      : `/radio/${qrData.frequency}`;

    Swal.fire({
      title: 'Destino actualizado (mock)',
      html: `
        <div style="text-align:left;font-size:13px">
          <p style="margin-bottom:8px">El QR de <strong>${qrData.radioName}</strong> ahora redirige a:</p>
          <div style="background:#f3f4f6;padding:10px;border-radius:8px">
            <p style="font-weight:600;color:#111827;margin:0">${label}</p>
            <p style="font-size:11px;color:#6b7280;margin:4px 0 0">${url}</p>
          </div>
          <p style="font-size:10px;color:#9ca3af;margin-top:8px"><i class="ri-information-line"></i> La URL permanente <strong>NO</strong> cambia. Solo cambia el destino de redireccion.</p>
        </div>
      `,
      icon: 'success',
      confirmButtonText: 'Entendido',
      customClass: {
        popup: 'rounded-2xl',
        title: 'font-bold text-sm',
        confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap bg-foreground-950 text-background-50',
        htmlContainer: 'text-sm',
      },
      buttonsStyling: false,
    });
    setShowDestEditor(false);
  }, [destType, customDomainInput, qrData]);

  /* ── Not found ── */
  if (!qrData) {
    return (
      <div className="min-h-screen bg-background-100">
        <div className="flex">
          <SASidebar />
          <main className="flex-1 min-w-0 flex items-center justify-center p-8">
            <div className="text-center max-w-sm">
              <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mx-auto mb-4">
                <i className="ri-qr-code-line text-foreground-300 text-3xl"></i>
              </div>
              <h2 className="text-foreground-950 font-bold text-base mb-1">Radio no encontrada</h2>
              <p className="text-foreground-400 text-xs mb-5">
                No se encontro una radio con ID <strong>{radioId}</strong>. Verifica que el ID sea correcto.
              </p>
              <Link
                to="/sa/radios"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line text-xs"></i>
                Volver a Radios
              </Link>
            </div>
          </main>
        </div>
      </div>
    );
  }

  /* ── QR Page ── */
  return (
    <div className="min-h-screen bg-background-100">
      <div className="flex">
        <SASidebar />
        <main className="flex-1 min-w-0">
          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5 max-w-[1100px] mx-auto">
            {/* Breadcrumbs */}
            <div className="flex items-center gap-1.5 text-[10px] text-foreground-400">
              <Link to="/sa/dashboard" className="hover:text-foreground-600 transition-colors cursor-pointer">Dashboard</Link>
              <i className="ri-arrow-right-s-line text-[9px]"></i>
              <Link to="/sa/radios" className="hover:text-foreground-600 transition-colors cursor-pointer">Radios</Link>
              <i className="ri-arrow-right-s-line text-[9px]"></i>
              <span className="text-foreground-500 font-medium">QR de {qrData.radioName}</span>
            </div>

            {/* Page header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-background-100 overflow-hidden shrink-0">
                  <img src={qrData.logoUrl} alt={qrData.radioName} className="w-full h-full object-cover" />
                </div>
                <div>
                  <h2 className="text-foreground-950 font-bold text-base">QR de {qrData.radioName}</h2>
                  <p className="text-foreground-400 text-xs">{qrData.frequency} MHz</p>
                </div>
              </div>
              <Link
                to="/sa/radios"
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-50 border border-background-200 text-foreground-600 text-xs font-medium hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line text-xs"></i>
                Volver a Radios
              </Link>
            </div>

            {/* Permanent URL notice */}
            <div className="bg-accent-50 border border-accent-200 rounded-2xl p-4 flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center shrink-0 mt-0.5">
                <i className="ri-information-line text-accent-500 text-sm"></i>
              </div>
              <div>
                <p className="text-accent-800 text-xs font-bold mb-0.5">URL Permanente</p>
                <p className="text-accent-600/80 text-[11px] leading-relaxed">
                  Este QR nunca apunta directo a la frecuencia ni dominio. Siempre usa <strong>radiofrecuenciaweb.com/r/{qrData.redirectCode}</strong> — una URL que no cambia jamas.
                </p>
              </div>
            </div>

            {/* QR Card */}
            <div className="bg-background-50 border border-background-200 rounded-2xl p-6 lg:p-8">
              <div className="flex flex-col lg:flex-row gap-8 items-center">
                {/* QR Image */}
                <div className="shrink-0">
                  <div className="w-56 h-56 rounded-2xl bg-white border border-background-200 p-4 flex items-center justify-center overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=Minimalist%20QR%20code%20illustration%20on%20pure%20white%20background%2C%20elegant%20clean%20modern%20technology%20design%2C%20dark%20geometric%20pattern%20with%20subtle%20brand%20frame%20border%2C%20professional%20corporate%20style%2C%20high%20contrast%20monochrome%2C%20digital%20identity%20element%20isolated&width=400&height=400&seq=sa-radio-qr-permanent-code&orientation=squarish"
                      alt={`QR Permanente ${qrData.permanentUrl}`}
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>

                {/* QR Info */}
                <div className="flex-1 min-w-0 space-y-4 w-full">
                  {/* Permanent URL display */}
                  <div>
                    <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider mb-1.5">URL Permanente del QR</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 bg-background-100 border border-background-200 rounded-xl px-4 py-2.5 text-foreground-800 text-sm font-mono break-all">
                        https://{qrData.permanentUrl}
                      </code>
                      <button
                        onClick={handleCopyUrl}
                        className="shrink-0 w-10 h-10 rounded-xl bg-foreground-950 text-background-50 flex items-center justify-center cursor-pointer hover:bg-foreground-800 transition-all active:scale-95 whitespace-nowrap"
                        title="Copiar URL"
                      >
                        <i className="ri-file-copy-line text-sm"></i>
                      </button>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={handleCopyUrl}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-file-copy-line text-xs"></i>
                      Copiar URL
                    </button>
                    <button
                      onClick={handleDownloadPng}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-background-100 border border-background-200 text-foreground-700 text-xs font-bold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-download-line text-xs"></i>
                      PNG
                    </button>
                    <button
                      onClick={handleDownloadSvg}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-background-100 border border-background-200 text-foreground-700 text-xs font-bold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-download-line text-xs"></i>
                      SVG
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Current destination card */}
            <div className="bg-background-50 border border-background-200 rounded-2xl p-5 lg:p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-foreground-950 font-bold text-sm">Destino Actual</h3>
                  <p className="text-foreground-400 text-[10px] mt-0.5">
                    A donde redirige el QR cuando alguien lo escanea
                  </p>
                </div>
                <button
                  onClick={() => {
                    setDestType(qrData.currentDestination.type);
                    setCustomDomainInput(qrData.customDomain || '');
                    setShowDestEditor(!showDestEditor);
                  }}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-edit-line text-xs"></i>
                  Cambiar destino
                </button>
              </div>

              <div className="flex items-center gap-3 p-4 rounded-xl bg-background-100">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${qrData.currentDestination.type === 'custom_domain' ? 'bg-emerald-100' : 'bg-secondary-100'}`}>
                  <i className={`text-sm ${qrData.currentDestination.type === 'custom_domain' ? 'ri-global-line text-emerald-600' : 'ri-radio-line text-secondary-600'}`}></i>
                </div>
                <div className="min-w-0">
                  <p className="text-foreground-800 text-xs font-bold">{qrData.currentDestination.label}</p>
                  <p className="text-foreground-400 text-[11px] truncate">{qrData.currentDestination.url}</p>
                </div>
                {qrData.hasCustomDomain && (
                  <span className="ml-auto shrink-0 px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-700 text-[10px] font-bold">Dominio propio</span>
                )}
              </div>

              {/* Destination editor */}
              {showDestEditor && (
                <div className="mt-4 p-4 rounded-xl bg-background-100 border border-background-200 space-y-3">
                  <p className="text-foreground-700 text-xs font-bold">Editar destino de redireccion</p>

                  <div className="flex gap-2">
                    <button
                      onClick={() => setDestType('rfe_page')}
                      className={`flex-1 flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                        destType === 'rfe_page'
                          ? 'bg-secondary-500 text-background-50'
                          : 'bg-white border border-background-200 text-foreground-500 hover:border-foreground-300'
                      }`}
                    >
                      <i className="ri-radio-line text-xs"></i>
                      Perfil RFW
                    </button>
                    <button
                      onClick={() => setDestType('custom_domain')}
                      className={`flex-1 flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                        destType === 'custom_domain'
                          ? 'bg-secondary-500 text-background-50'
                          : 'bg-white border border-background-200 text-foreground-500 hover:border-foreground-300'
                      }`}
                    >
                      <i className="ri-global-line text-xs"></i>
                      Dominio propio
                    </button>
                  </div>

                  {destType === 'custom_domain' && (
                    <div>
                      <label className="block text-foreground-400 text-[10px] font-semibold mb-1">Dominio (sin https://)</label>
                      <input
                        type="text"
                        value={customDomainInput}
                        onChange={(e) => setCustomDomainInput(e.target.value)}
                        placeholder="miradio.com"
                        className="w-full px-3 py-2 bg-white border border-background-200 rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors"
                      />
                    </div>
                  )}

                  <div className="flex items-center gap-2 pt-1">
                    <button
                      onClick={handleUpdateDestination}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-check-line text-xs"></i>
                      Guardar destino
                    </button>
                    <button
                      onClick={() => setShowDestEditor(false)}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-white border border-background-200 text-foreground-500 text-xs font-medium hover:bg-background-50 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* How it works */}
            <div className="bg-background-50 border border-background-200 rounded-2xl p-5 lg:p-6">
              <h3 className="text-foreground-950 font-bold text-sm mb-4">Como funciona</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-lg bg-foreground-950 flex items-center justify-center shrink-0">
                    <span className="text-background-50 text-[10px] font-bold">1</span>
                  </div>
                  <div>
                    <p className="text-foreground-800 text-xs font-bold mb-0.5">QR siempre apunta a:</p>
                    <p className="text-foreground-400 text-[11px] leading-relaxed">
                      <code className="bg-background-100 px-1.5 py-0.5 rounded text-[10px]">radiofrecuenciaweb.com/r/{qrData.redirectCode}</code>
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-lg bg-foreground-950 flex items-center justify-center shrink-0">
                    <span className="text-background-50 text-[10px] font-bold">2</span>
                  </div>
                  <div>
                    <p className="text-foreground-800 text-xs font-bold mb-0.5">El sistema decide:</p>
                    <p className="text-foreground-400 text-[11px] leading-relaxed">
                      Si tiene dominio propio activo, redirige alla. Si no, al perfil en RadioFrecuenciaWeb.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-lg bg-foreground-950 flex items-center justify-center shrink-0">
                    <span className="text-background-50 text-[10px] font-bold">3</span>
                  </div>
                  <div>
                    <p className="text-foreground-800 text-xs font-bold mb-0.5">Nunca cambia:</p>
                    <p className="text-foreground-400 text-[11px] leading-relaxed">
                      Cambia de plan, dominio o frecuencia. El QR siempre funciona igual.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}